import type { Scan, ScanReviewGroup } from "@hobbyshopify/backend-client";
import { Rx, RxList } from "subtree.js/core";
import { idleLoad, type LoadStatus } from "../../core/primitives/load-status";
import type { ReviewDraft } from "../../core/primitives/review-row";
import type { Capture } from "../../core/services/scan-session";

/** The `busyKey` while "Confirm all priced" runs. */
export const CONFIRM_ALL_KEY = "__priced__";

/**
 * What Review shows: the first read, a failed read with nothing to list, nothing waiting, or the list. The cards on
 * their way show while the queue is read (`readingUnder`), and a scan no card was recognised in is no empty queue.
 */
export function reviewShows(v: {
  readonly status: LoadStatus;
  readonly groups: number;
  readonly captures: number;
  readonly unrecognised: number;
}): { readonly body: "loading" | "failed" | "empty" | "list"; readonly readingUnder: boolean } {
  const reading = (v.status.kind === "idle" || v.status.kind === "loading") && v.groups === 0;
  if (reading && v.captures === 0) return { body: "loading", readingUnder: false };
  if (v.status.kind === "error" && v.groups === 0 && v.captures === 0) return { body: "failed", readingUnder: false };
  if (v.groups === 0 && v.captures === 0 && v.unrecognised === 0) return { body: "empty", readingUnder: false };
  return { body: "list", readingUnder: reading };
}

export class DraftsState {
  readonly status = new Rx<LoadStatus>(idleLoad);
  readonly groups = new RxList<ScanReviewGroup>();
  readonly unrecognised = new RxList<Scan>();
  readonly actionError = new Rx<string | null>(null);
  /** The group a confirm or reject is running for, or `CONFIRM_ALL_KEY`; null when idle. */
  readonly busyKey = new Rx<string | null>(null);
  /** What the reviewer typed on each row, by group key. */
  readonly drafts = new Rx<Readonly<Record<string, ReviewDraft>>>({});
  /** The candidate a pick is running for. */
  readonly picking = new Rx<{ key: string; productId: string } | null>(null);
  /** This browser's cards scanned for the shop still on their way, newest first, and those landed until read. */
  readonly captures = new RxList<Capture>();
}

export abstract class DraftsActions {
  abstract confirm(group: ScanReviewGroup): Promise<void>;
  abstract reject(group: ScanReviewGroup): Promise<void>;
  abstract review(group: ScanReviewGroup): void;
  /** Confirms, one after another, the catalogued rows and every row whose price was typed; customers' scans never. */
  abstract confirmAllPriced(): Promise<void>;
  abstract setPrice(group: ScanReviewGroup, price: string): void;
  abstract setQuantity(group: ScanReviewGroup, quantity: number): void;
  abstract setApproveCodes(group: ScanReviewGroup, approve: boolean): void;
  abstract pick(group: ScanReviewGroup, productId: string): Promise<void>;
  abstract dismissError(): void;
  abstract retry(): void;
  /** A card whose scan failed, sent again. */
  abstract retryCapture(id: string): void;
  /** A card whose scan failed, dropped. */
  abstract discardCapture(id: string): void;
}
